import useAdminAccess from "hooks/useAdminAccess";
import { useState } from "react";
import { useQuery } from "react-query";
import { showSuccess } from "utilities/misc";
import { get, post } from "utilities/network";

type Config = {
  mode: "legacy" | "disabled" | "provider" | "ranges";
  provider?: string;
  ranges?: { merchant: string; min: number; max: number }[];
};
type Route = {
  id: string;
  country: string;
  currency: string;
  direction: string;
  method: string;
  providers: string[];
  providerNames?: Record<string, string>;
  config: Config;
};
const countries: Record<string, string> = {
  NG: "Nigeria",
  GH: "Ghana",
  KE: "Kenya",
};
function RouteEditor({
  route,
  onSaved,
  canEdit,
}: {
  route: Route;
  onSaved: () => Promise<unknown>;
  canEdit: boolean;
}) {
  const [config, setConfig] = useState(route.config);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const dirty = JSON.stringify(config) !== JSON.stringify(route.config);
  async function save() {
    setSaving(true);
    setError("");
    try {
      await post("admin/transactions/paymentRoutes", {
        routeId: route.id,
        config,
      });
      await onSaved();
      showSuccess("Payment route updated");
    } catch (e) {
      setError(
        (e as any)?.message || "Could not save this route. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  }
  const title = `${countries[route.country]} · ${route.direction === "payin" ? "Pay-in" : "Payout"} · ${route.method === "bank" ? "Bank transfer" : "Mobile money"}`;
  const selectClass =
    "w-full rounded-lg border border-gray-300 bg-white p-2 text-gray-900";
  return (
    <fieldset
      disabled={!canEdit || saving}
      className="p-4 border border-gray-200 rounded-xl"
    >
      <legend className="px-1 font-medium">
        {title} ({route.currency})
      </legend>
      <label className="block">
        Routing
        <select
          aria-label={`${title} routing`}
          className={selectClass}
          value={config.mode}
          onChange={(e) => {
            const mode = e.target.value as Config["mode"];
            setConfig(
              mode === "provider"
                ? { mode, provider: route.providers[0] }
                : mode === "ranges"
                  ? {
                      mode,
                      ranges: [
                        { merchant: route.providers[0], min: 0, max: 1000 },
                      ],
                    }
                  : { mode },
            );
          }}
        >
          <option value="disabled">Unavailable for new requests</option>
          {route.direction === "payout" && (
            <option value="legacy">Existing payout rules</option>
          )}
          <option value="provider">One provider</option>
          {!(route.direction === "payin" && route.method === "bank") && (
            <option value="ranges">Provider by amount</option>
          )}
        </select>
      </label>
      {config.mode === "provider" && (
        <label className="block mt-3">
          Provider
          <select
            aria-label={`${title} provider`}
            className={selectClass}
            value={config.provider}
            onChange={(e) =>
              setConfig({ mode: "provider", provider: e.target.value })
            }
          >
            {route.providers.map((key) => (
              <option key={key} value={key}>
                {route.providerNames?.[key] || key}
              </option>
            ))}
          </select>
        </label>
      )}
      {config.mode === "ranges" && (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-gray-600">
            Ranges include both endpoints. Amounts outside these ranges are
            unavailable.
          </p>
          {config.ranges?.map((range, index) => (
            <div key={index} className="grid grid-cols-1 gap-2 sm:grid-cols-4">
              <select
                aria-label={`${title} range ${index + 1} provider`}
                className={selectClass}
                value={range.merchant}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    ranges: config.ranges?.map((r, i) =>
                      i === index ? { ...r, merchant: e.target.value } : r,
                    ),
                  })
                }
              >
                {route.providers.map((key) => (
                  <option key={key} value={key}>
                    {route.providerNames?.[key] || key}
                  </option>
                ))}
              </select>
              {(["min", "max"] as const).map((field) => (
                <input
                  key={field}
                  aria-label={`${title} range ${index + 1} ${field}`}
                  className={selectClass}
                  type="number"
                  min="0"
                  step="0.01"
                  value={range[field]}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      ranges: config.ranges?.map((r, i) =>
                        i === index
                          ? { ...r, [field]: Number(e.target.value) }
                          : r,
                      ),
                    })
                  }
                />
              ))}
              <button
                type="button"
                onClick={() =>
                  setConfig({
                    ...config,
                    ranges: config.ranges?.filter((_, i) => i !== index),
                  })
                }
              >
                Remove range
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setConfig({
                ...config,
                ranges: [
                  ...(config.ranges || []),
                  { merchant: route.providers[0], min: 0, max: 1000 },
                ],
              })
            }
          >
            Add amount range
          </button>
        </div>
      )}
      {config.mode === "legacy" && (
        <p className="mt-2 text-sm text-gray-600">
          Uses the existing currency override and amount rules, subject to
          provider compatibility.
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-red-600">
          {error}
        </p>
      )}
      {canEdit && (
        <button
          className="px-4 py-2 mt-3 text-white bg-black rounded-lg disabled:opacity-40"
          type="button"
          disabled={!dirty || saving}
          onClick={save}
        >
          {saving ? "Saving…" : "Save route"}
        </button>
      )}
    </fieldset>
  );
}
export default function PaymentRoutes() {
  const { can } = useAdminAccess();
  const { data, isLoading, isError, refetch } = useQuery<{ routes: Route[] }>(
    "paymentRoutes",
    () => get("admin/transactions/paymentRoutes"),
  );
  return (
    <section className="my-8">
      <h2 className="text-xl">Pay-in and payout routing</h2>
      <p className="my-3 text-gray-600">
        Choose providers independently for each country and payment method.
        Changes apply to new requests; payments already started keep their
        original provider. Only supported integrations are listed.
      </p>
      {isLoading && <p>Loading payment routes…</p>}
      {isError && (
        <p role="alert">
          Could not load payment routes.{" "}
          <button onClick={() => refetch()}>Retry</button>
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {data?.routes.map((route) => (
          <RouteEditor
            key={`${route.id}:${JSON.stringify(route.config)}`}
            route={route}
            canEdit={can("transactions.paymentMerchant.edit")}
            onSaved={refetch}
          />
        ))}
      </div>
    </section>
  );
}
